import { useEffect, useState } from 'react';
import type { FC } from 'react';

import { FormattedMessage } from 'react-intl';

import classNames from 'classnames';

import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';

import { Portal } from 'mastodon/components/popover/portal';
import {
  AccountRow,
  SwitchAccountNotes,
  useAccountSwitcher,
} from 'mastodon/features/ui/components/switch_account_modal';
import { useOnClickOutside } from 'mastodon/hooks/useOnClickOutside';

import classes from './switch_account_bubble.module.scss';

const ARROW_SIDES = {
  right: 'left',
  left: 'right',
  top: 'bottom',
  bottom: 'top',
} as const;

/**
 * The accounts to switch to, in a speech bubble pointing at the sidebar's
 * "Switch account" item (instead of Mastodon's modal). Colors, corners and
 * name sizes come from the theme.
 */
export const SwitchAccountBubble: FC<{
  reference: HTMLElement | null;
  // The button that opens it, whose clicks are not clicks outside
  trigger: HTMLElement | null;
  onClose: () => void;
}> = ({ reference, trigger, onClose }) => {
  const [floating, setFloating] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const {
    accountId,
    accountIds,
    canAddAccount,
    pending,
    error,
    handleSelect,
    handleAdd,
  } = useAccountSwitcher();

  const { floatingStyles, middlewareData, placement } = useFloating({
    elements: { reference, floating },
    placement: 'right',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(18),
      flip({ fallbackPlacements: ['bottom', 'top'] }),
      shift({ padding: 8 }),
      arrow({ element: arrowElement, padding: 10 }),
    ],
  });

  useOnClickOutside([floating, trigger], onClose);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  const side = placement.split('-')[0] as keyof typeof ARROW_SIDES;
  const arrowData = middlewareData.arrow;

  return (
    <Portal>
      <div
        ref={setFloating}
        style={floatingStyles}
        className={classes.bubble}
        role='dialog'
        aria-labelledby='switch-account-bubble-title'
      >
        <h2 id='switch-account-bubble-title' className={classes.title}>
          <FormattedMessage
            id='account_switcher.title'
            defaultMessage='Switch account'
          />
        </h2>

        <ul className={classes.list}>
          {accountIds.map((id) => (
            <AccountRow
              key={id}
              accountId={id}
              current={id === accountId}
              disabled={pending}
              onSelect={handleSelect}
            />
          ))}
        </ul>

        {canAddAccount && (
          <button
            type='button'
            className={classes.add}
            onClick={handleAdd}
            disabled={pending}
          >
            <FormattedMessage
              id='account_switcher.add_account'
              defaultMessage='Add an existing account'
            />
          </button>
        )}

        <SwitchAccountNotes canAddAccount={canAddAccount} error={error} />

        <div
          ref={setArrowElement}
          className={classNames(classes.arrow, classes[`arrow-${side}`])}
          style={{
            left: arrowData?.x,
            top: arrowData?.y,
            [ARROW_SIDES[side]]: -7,
          }}
          aria-hidden
        />
      </div>
    </Portal>
  );
};
